import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, CheckCircle, XCircle, User, Mail, Phone,
  Tag, Clock, Save, RefreshCw, FileText, DollarSign
} from 'lucide-react';
import adminService from '../../services/admin/adminService';
import { Dispute, ResolveDisputeRequest } from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const DisputeDetails: React.FC = () => {
  const { disputeId } = useParams<{ disputeId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dispute, setDispute] = useState<Dispute | null>(null);
  
  // Form states
  const [resolution, setResolution] = useState<'resolved' | 'rejected'>('resolved');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionTaken, setActionTaken] = useState('');

  useEffect(() => {
    if (disputeId) {
      loadDisputeDetails();
    }
  }, [disputeId]);

  const loadDisputeDetails = async () => {
    if (!disputeId) return;
    setLoading(true);
    try {
      const data = await adminService.getDisputeById(Number(disputeId));
      setDispute(data);
      if (data.resolution) {
        setResolution(data.resolution as 'resolved' | 'rejected');
      }
      if (data.resolution_notes) {
        setResolutionNotes(data.resolution_notes);
      }
      if (data.action_taken) {
        setActionTaken(data.action_taken);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load dispute details');
      navigate('/admin/support');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveDispute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disputeId || !resolutionNotes.trim() || resolutionNotes.length < 10) {
      toast.error('Resolution notes must be at least 10 characters');
      return;
    }

    setSaving(true);
    try {
      const data: ResolveDisputeRequest = {
        dispute_id: Number(disputeId),
        resolution: resolution,
        resolution_notes: resolutionNotes,
        action_taken: actionTaken || undefined
      };
      await adminService.resolveDispute(data);
      toast.success(`Dispute ${resolution === 'resolved' ? 'resolved' : 'rejected'} successfully`);
      await loadDisputeDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resolve dispute');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      pending: 'bg-orange-100 text-orange-800',
      under_review: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const badges: Record<string, string> = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return badges[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  if (!dispute) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-secondary-600">Dispute not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/support')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-secondary-900">{dispute.dispute_number}</h1>
          <p className="text-secondary-600 mt-1">{dispute.subject}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dispute Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4">Dispute Details</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary-600">Description</label>
                <p className="mt-1 text-secondary-900">{dispute.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-600">Status</label>
                  <div className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(dispute.status)}`}>
                      {dispute.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600">Priority</label>
                  <div className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(dispute.priority)}`}>
                      {dispute.priority}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600">Type</label>
                  <p className="mt-1 text-secondary-900 capitalize">{dispute.dispute_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600">Created</label>
                  <p className="mt-1 text-secondary-900">
                    {new Date(dispute.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              {dispute.related_transaction_id && (
                <div>
                  <label className="text-sm font-medium text-secondary-600">Related Transaction</label>
                  <p className="mt-1 text-secondary-900">{dispute.related_transaction_id}</p>
                </div>
              )}
              {dispute.related_settlement_id && (
                <div>
                  <label className="text-sm font-medium text-secondary-600">Related Settlement</label>
                  <p className="mt-1 text-secondary-900">#{dispute.related_settlement_id}</p>
                </div>
              )}
            </div>
          </div>

          {/* Resolution Info */}
          {dispute.resolution && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-4">Resolution</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-secondary-600">Resolution</label>
                  <div className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      dispute.resolution === 'resolved' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {dispute.resolution}
                    </span>
                  </div>
                </div>
                {dispute.resolution_notes && (
                  <div>
                    <label className="text-sm font-medium text-secondary-600">Resolution Notes</label>
                    <p className="mt-1 text-secondary-900">{dispute.resolution_notes}</p>
                  </div>
                )}
                {dispute.action_taken && (
                  <div>
                    <label className="text-sm font-medium text-secondary-600">Action Taken</label>
                    <p className="mt-1 text-secondary-900">{dispute.action_taken}</p>
                  </div>
                )}
                {dispute.resolved_at && (
                  <div>
                    <label className="text-sm font-medium text-secondary-600">Resolved At</label>
                    <p className="mt-1 text-secondary-900">
                      {new Date(dispute.resolved_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resolve Dispute Form */}
          {dispute.status !== 'resolved' && dispute.status !== 'rejected' && dispute.status !== 'closed' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-4">Resolve Dispute</h2>
              <form onSubmit={handleResolveDispute} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-secondary-600 mb-2 block">Resolution</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="resolved"
                        checked={resolution === 'resolved'}
                        onChange={(e) => setResolution(e.target.value as 'resolved' | 'rejected')}
                        className="w-4 h-4 text-primary-500 focus:ring-primary-500"
                      />
                      <CheckCircle className="text-green-600" size={20} />
                      <span className="text-secondary-900">Resolved</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="rejected"
                        checked={resolution === 'rejected'}
                        onChange={(e) => setResolution(e.target.value as 'resolved' | 'rejected')}
                        className="w-4 h-4 text-primary-500 focus:ring-primary-500"
                      />
                      <XCircle className="text-red-600" size={20} />
                      <span className="text-secondary-900">Rejected</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600 mb-2 block">
                    Resolution Notes <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Provide detailed resolution notes (minimum 10 characters)..."
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    minLength={10}
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    {resolutionNotes.length}/10 characters minimum
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-600 mb-2 block">
                    Action Taken (Optional)
                  </label>
                  <textarea
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    placeholder="Describe any actions taken to resolve this dispute..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving || resolutionNotes.length < 10}
                  className={`w-full px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                    resolution === 'resolved'
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  <Save size={18} />
                  {saving ? 'Processing...' : `${resolution === 'resolved' ? 'Resolve' : 'Reject'} Dispute`}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Creator Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Creator Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User size={18} className="text-secondary-500" />
                <span className="text-secondary-900">{dispute.creator_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-secondary-500" />
                <span className="text-secondary-900">{dispute.creator_email}</span>
              </div>
              {dispute.creator_phone && (
                <div className="flex items-center gap-2">
                  <Phone size={18} className="text-secondary-500" />
                  <span className="text-secondary-900">{dispute.creator_phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Tag size={18} className="text-secondary-500" />
                <span className="text-secondary-900 capitalize">{dispute.created_by_type}</span>
              </div>
            </div>
          </div>

          {/* Assignment Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Assignment</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-secondary-600">Assigned To</label>
                <p className="mt-1 text-secondary-900">
                  {dispute.assigned_admin_name || 'Unassigned'}
                </p>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Timeline</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-secondary-500 mt-1" />
                <div>
                  <p className="text-sm font-medium text-secondary-900">Created</p>
                  <p className="text-xs text-secondary-500">
                    {new Date(dispute.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              {dispute.resolved_at && (
                <div className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-green-600 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">Resolved</p>
                    <p className="text-xs text-secondary-500">
                      {new Date(dispute.resolved_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-secondary-500 mt-1" />
                <div>
                  <p className="text-sm font-medium text-secondary-900">Last Updated</p>
                  <p className="text-xs text-secondary-500">
                    {new Date(dispute.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisputeDetails;

